import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveAssetUrl } from '../lib/assets';
import { regionLabel } from '../lib/regionNames';
import { CLASSIFIED_CATEGORY_MAP, resolveCat, formatClassifiedPrice } from '../data/classifiedsCategories';
import { ArrowLeft, Plus, Eye, Edit, Trash2, CheckCircle2, RotateCcw, Loader2, Tag, ImageOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyClassifiedsPage() {
  const { tr } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/classifieds/mine`); setItems(data.classifieds || []); setActiveCount(data.active_count || 0); }
    catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (it, status) => {
    setBusyId(it.id);
    try { await axios.patch(`${API}/classifieds/${it.id}/status`, { status }); await load(); toast.success(status === 'sold' ? tr('Atzīmēts kā pārdots', 'Marked sold', 'Отмечено продано') : tr('Aktivizēts', 'Reactivated', 'Активировано')); }
    catch { toast.error(tr('Kļūda', 'Error', 'Ошибка')); }
    setBusyId(null);
  };

  const del = async (it) => {
    if (!window.confirm(tr('Dzēst šo sludinājumu?', 'Delete this listing?', 'Удалить это объявление?'))) return;
    setBusyId(it.id);
    try { await axios.delete(`${API}/classifieds/${it.id}`); await load(); toast.success(tr('Dzēsts', 'Deleted', 'Удалено')); }
    catch { toast.error(tr('Kļūda', 'Error', 'Ошибка')); setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="my-classifieds-page">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-5" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>

        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#E6F2F0] flex items-center justify-center"><Tag className="w-6 h-6 text-[#2F7E79]" /></div>
            <div>
              <h1 className="font-outfit text-3xl font-bold text-[#121212]">{tr('Mani sludinājumi', 'My listings', 'Мои объявления')}</h1>
              <p className="text-sm text-[#5C5C5C]">{activeCount} {tr('aktīvi', 'active', 'активных')}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/sludinajumi/jauns')} className="bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2 rounded-full" data-testid="add-classified-btn">
            <Plus className="w-4 h-4" /> {tr('Pievienot', 'Add', 'Добавить')}
          </Button>
        </div>

        <button onClick={() => navigate('/sludinajumi/darijumi')}
          className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[#2F7E79] hover:underline" data-testid="my-deals-link">
          <ShieldCheck className="w-4 h-4" /> {tr('Mani darījumi (pirkumi un pārdošana)', 'My deals (buying & selling)', 'Мои сделки')}
        </button>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center bg-white border border-[#E5E4E0] rounded-2xl" data-testid="my-classifieds-empty">
            <div className="text-5xl mb-3">🧳</div>
            <p className="text-[#5C5C5C] mb-4">{tr('Tev vēl nav sludinājumu.', 'You have no listings yet.', 'У вас пока нет объявлений.')}</p>
            <Button onClick={() => navigate('/sludinajumi/jauns')} className="bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2 rounded-full">
              <Plus className="w-4 h-4" /> {tr('Izveidot pirmo', 'Create the first', 'Создать первое')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const cat = resolveCat(it.category);
              const img = it.images && it.images.length ? resolveAssetUrl(it.images[0]) : null;
              const sold = it.status === 'sold';
              const pending = it.status === 'pending';
              return (
                <div key={it.id} className="bg-white border border-[#E5E4E0] rounded-2xl p-3 flex gap-3" data-testid={`my-classified-${it.id}`}>
                  <button onClick={() => navigate(`/sludinajums/${it.slug || it.id}`)} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-[#F3F2EE] flex-shrink-0 relative">
                    {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#C7C5BF]"><ImageOff className="w-6 h-6" /></div>}
                    {sold && <span className="absolute inset-0 bg-[#121212]/60 flex items-center justify-center text-white text-[10px] font-bold">{tr('PĀRDOTS', 'SOLD', 'ПРОДАНО')}</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => navigate(`/sludinajums/${it.slug || it.id}`)} className="text-left">
                        <h3 className="font-outfit font-semibold text-[#121212] truncate">{it.title}</h3>
                      </button>
                      <span className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${sold ? 'bg-[#121212] text-white' : pending ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-testid={`status-badge-${it.id}`}>
                        {sold ? tr('Pārdots', 'Sold', 'Продано') : pending ? tr('Gaida apstiprinājumu', 'Pending approval', 'Ожидает') : tr('Aktīvs', 'Active', 'Активно')}
                      </span>
                    </div>
                    <div className="text-[#2F7E79] font-bold text-sm mt-0.5">{formatClassifiedPrice(it, tr)}</div>
                    <div className="flex items-center gap-3 text-xs text-[#8A8A8A] mt-1">
                      {cat && <span>{cat.emoji} {tr(cat.lv, cat.en, cat.ru)}</span>}
                      {it.region && <span>{regionLabel(it.region)}</span>}
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {it.views || 0}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button onClick={() => navigate(`/sludinajumi/${it.id}/labot`)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5C5C5C] hover:text-[#2F7E79] px-2 py-1 rounded-lg hover:bg-[#F3F2EE]" data-testid={`edit-${it.id}`}><Edit className="w-3.5 h-3.5" /> {tr('Rediģēt', 'Edit', 'Редакт.')}</button>
                      {!pending && (sold
                        ? <button onClick={() => setStatus(it, 'active')} disabled={busyId === it.id} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5C5C5C] hover:text-green-700 px-2 py-1 rounded-lg hover:bg-[#F3F2EE]" data-testid={`reactivate-${it.id}`}><RotateCcw className="w-3.5 h-3.5" /> {tr('Aktivizēt', 'Reactivate', 'Активир.')}</button>
                        : <button onClick={() => setStatus(it, 'sold')} disabled={busyId === it.id} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5C5C5C] hover:text-[#121212] px-2 py-1 rounded-lg hover:bg-[#F3F2EE]" data-testid={`mark-sold-${it.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> {tr('Pārdots', 'Sold', 'Продано')}</button>)}
                      <button onClick={() => del(it)} disabled={busyId === it.id} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50" data-testid={`delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /> {tr('Dzēst', 'Delete', 'Удалить')}</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
